"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { checkOrderStatus, type OrderStatus } from "@/lib/api"

interface UseOrderPollingOptions {
  pollingInterval?: number // ms
  maxAttempts?: number
  onStatusChange?: (status: OrderStatus) => void
  secaoId?: string
  estabelecimentoId?: string
}

export function useOrderPolling(
  orderId: string | null,
  options: UseOrderPollingOptions = {}
) {
  const {
    pollingInterval = 5000, // 5 segundos por padrão
    maxAttempts = 120, // Máx 10 minutos (120 * 5s)
    onStatusChange,
    secaoId,
    estabelecimentoId,
  } = options

  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const attemptCountRef = useRef(0)
  const mountedRef = useRef(true)

  // Função para buscar status do pedido
  const fetchStatus = useCallback(async (): Promise<OrderStatus | null> => {
    if (!orderId || !mountedRef.current) return null

    setIsLoading(true)
    setError(null)

    try {
      console.log(
        `[useOrderPolling] 🔄 Polling status do pedido ${orderId}... (tentativa ${attemptCountRef.current + 1})`,
        {
          secaoId,
          estabelecimentoId,
        }
      )
      const status = await checkOrderStatus(orderId)

      if (!mountedRef.current) return null

      console.log(`[useOrderPolling] ✅ Status recebido:`, status.status)

      // Atualizar estado
      setOrderStatus(status)

      // Callback quando status muda
      if (onStatusChange) {
        onStatusChange(status)
      }

      // Se pedido foi entregue/finalizado, parar polling
      if (status.status === "Entregue" || status.status === "Finalizado") {
        console.log(`[useOrderPolling] ✅ Pedido finalizado! Parando polling.`)
        setIsPolling(false)
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      }

      return status
    } catch (err) {
      console.error(`[useOrderPolling] ❌ Erro ao buscar status:`, err)
      if (!mountedRef.current) return null

      setError(err instanceof Error ? err.message : "Erro ao buscar status")

      // Se chegou ao máximo de tentativas, parar polling
      if (attemptCountRef.current >= maxAttempts) {
        console.log(`[useOrderPolling] ⚠️ Máximo de tentativas atingido. Parando polling.`)
        setIsPolling(false)
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      }

      return null
    } finally {
      setIsLoading(false)
    }
  }, [orderId, onStatusChange, maxAttempts])

  // Iniciar polling quando orderId mudar
  useEffect(() => {
    if (!orderId) {
      setOrderStatus(null)
      setIsPolling(false)
      attemptCountRef.current = 0
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      return
    }

    // Buscar status imediatamente
    attemptCountRef.current = 0
    setIsPolling(true)
    fetchStatus()

    // Configurar polling periódico
    pollingIntervalRef.current = setInterval(() => {
      if (mountedRef.current && isPolling) {
        attemptCountRef.current++
        if (attemptCountRef.current >= maxAttempts) {
          console.log(`[useOrderPolling] ⚠️ Máximo de tentativas atingido.`)
          setIsPolling(false)
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
          return
        }
        fetchStatus()
      }
    }, pollingInterval)

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [orderId, pollingInterval, maxAttempts, fetchStatus])

  // Cleanup no unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [])

  // Função para parar polling manualmente
  const stopPolling = useCallback(() => {
    console.log(`[useOrderPolling] ⏹️ Parando polling manualmente`)
    setIsPolling(false)
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [])

  // Função para retomar polling manualmente
  const resumePolling = useCallback(() => {
    if (!orderId) return
    console.log(`[useOrderPolling] ▶️ Resumindo polling`)
    setIsPolling(true)
    attemptCountRef.current = 0
    fetchStatus()

    pollingIntervalRef.current = setInterval(() => {
      if (mountedRef.current && isPolling) {
        attemptCountRef.current++
        if (attemptCountRef.current >= maxAttempts) {
          setIsPolling(false)
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
          return
        }
        fetchStatus()
      }
    }, pollingInterval)
  }, [orderId, pollingInterval, maxAttempts, fetchStatus])

  return {
    orderStatus,
    isPolling,
    isLoading,
    error,
    fetchStatus,
    stopPolling,
    resumePolling,
    attemptCount: attemptCountRef.current,
  }
}
